import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, TrendingUp, Target, CheckCircle, Clock, FileText, 
  Lightbulb, ArrowRight, Sparkles, Download, Loader2, Key
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';

// Types for the comprehensive analysis structure
interface ScoreItem {
  before: number;
  after: number;
  change: string;
}

interface ProblemItem {
  their_problem: string;
  your_solution: string;
  where_it_appears: string;
  what_changed: string;
}

interface PositionChange {
  position: string;
  changes: string[];
}

interface AnalysisResults {
  title?: string;
  overview?: string;
  scores?: {
    overall?: ScoreItem;
    job_title_alignment?: ScoreItem;
    skills_match?: ScoreItem;
    experience_relevance?: ScoreItem;
    keyword_coverage?: ScoreItem;
    problem_solution_positioning?: ScoreItem;
  };
  problems_you_now_solve?: {
    summary?: string;
    problems?: ProblemItem[];
  };
  what_changed?: {
    career_summary?: {
      before?: string;
      after?: string;
      problems_now_named?: string[];
      metrics_highlighted?: string[];
    };
    career_highlights?: {
      before?: string;
      after?: string;
      new_micro_headings?: string[];
      key_changes?: string[];
    };
    skills_section?: {
      skills_added?: string[];
      skills_removed?: string[];
      why_it_matters?: string;
    };
    professional_experience?: {
      before?: string;
      after?: string;
      key_changes?: PositionChange[];
    };
  };
  keywords_added?: {
    summary?: string;
    coverage_percentage?: number;
    keywords_added?: string[];
    keywords_still_missing?: string[];
  };
  why_this_matters?: {
    before?: string;
    after?: string;
  };
  metadata?: {
    target_role?: string;
    target_company?: string;
    target_industry?: string;
    analysis_date?: string;
  };
}

interface ClaudeMatchAnalysisModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysisResults?: AnalysisResults;
  changesAnalysis?: any; // Legacy format for backward compatibility
  beforeScore?: number;
  afterScore?: number;
  jobTitle?: string;
  companyName?: string;
}

const ScoreCard: React.FC<{
  label: string;
  score?: ScoreItem;
  colorClass: string;
  icon?: React.ReactNode;
}> = ({ label, score, colorClass, icon }) => {
  if (!score) return null;
  
  return (
    <div className={`text-center p-4 rounded-lg border ${colorClass}`}>
      <div className="flex items-center justify-center gap-2 mb-2">
        {icon}
        <div className="text-2xl font-bold">{score.after}%</div>
      </div>
      <div className="text-xs text-muted-foreground mb-1">
        from {score.before}% <span className="text-emerald-600 font-medium">({score.change})</span>
      </div>
      <div className="text-sm font-medium">{label}</div>
    </div>
  );
};

export const ClaudeMatchAnalysisModal: React.FC<ClaudeMatchAnalysisModalProps> = ({
  open,
  onOpenChange,
  analysisResults,
  changesAnalysis,
  beforeScore,
  afterScore,
  jobTitle,
  companyName
}) => {
  const { toast } = useToast();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // === DEBUG LOGGING ===
  console.log('=== RESUME MATCH ANALYSIS MODAL DEBUG ===');
  console.log('Props received:');
  console.log('- analysisResults:', analysisResults);
  console.log('- changesAnalysis:', changesAnalysis);
  console.log('- beforeScore:', beforeScore, 'afterScore:', afterScore);
  
  // Explicit boolean detection of new format in each source
  const hasNewFormatInChanges = !!(changesAnalysis?.scores?.overall || changesAnalysis?.overview);
  const hasNewFormatInAnalysis = !!(analysisResults?.scores?.overall || analysisResults?.overview);
  
  console.log('Detection flags:');
  console.log('- hasNewFormatInChanges:', hasNewFormatInChanges);
  console.log('- hasNewFormatInAnalysis:', hasNewFormatInAnalysis);
  
  // Prioritize changesAnalysis if it has the new format, otherwise use analysisResults
  const effectiveAnalysisResults: AnalysisResults | undefined = hasNewFormatInChanges 
    ? changesAnalysis as AnalysisResults 
    : hasNewFormatInAnalysis 
      ? analysisResults 
      : analysisResults; // Fallback to analysisResults even if legacy
  
  // Use new structure if available, otherwise fall back to legacy
  const hasNewStructure = !!(effectiveAnalysisResults?.scores?.overall);
  const scores = effectiveAnalysisResults?.scores;
  const improvement = beforeScore && afterScore ? afterScore - beforeScore : 0;
  
  console.log('Final detection results:');
  console.log('- effectiveAnalysisResults:', effectiveAnalysisResults);
  console.log('- hasNewStructure:', hasNewStructure);
  console.log('- scores:', scores);
  console.log('=== END MODAL DEBUG ===');

  // PDF Export function
  const handleExportPdf = async () => {
    if (!contentRef.current) return;
    
    setIsExporting(true);
    
    try {
      // Dynamic imports for PDF generation
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      // Capture the content
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowHeight: contentRef.current.scrollHeight
      });
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename
      const sanitize = (str: string) => str?.replace(/[^a-zA-Z0-9]/g, '_') || '';
      const filename = `${sanitize(jobTitle) || 'Resume'}_${sanitize(companyName) || 'Analysis'}_Match_Analysis.pdf`;
      
      pdf.save(filename);
      
      toast({
        title: 'PDF Downloaded',
        description: 'Your resume analysis report has been saved.'
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Export Failed',
        description: 'Failed to generate PDF. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden">
        <DialogHeader className="flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <BarChart3 className="w-6 h-6 text-primary" />
              {effectiveAnalysisResults?.title || "Comprehensive Resume Analysis Report"}
            </DialogTitle>
            <DialogDescription className="text-base">
              {jobTitle && companyName 
                ? `Detailed breakdown of your resume performance for ${jobTitle} at ${companyName}`
                : "Detailed breakdown of your resume tailoring improvements"}
            </DialogDescription>
          </div>
          <Button
            onClick={handleExportPdf}
            disabled={isExporting}
            variant="outline"
            size="sm"
            className="gap-2 shrink-0"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isExporting ? 'Exporting...' : 'Download PDF'}
          </Button>
        </DialogHeader>
        
        <div ref={contentRef} className="flex-1 overflow-y-auto max-h-[80vh] space-y-6 p-2 bg-white">
          
          {/* Hero Score Section - Matches Main Page Design */}
          <div className="bg-gradient-to-r from-slate-50 to-emerald-50/30 rounded-2xl p-6 border border-slate-200">
            {/* Job Info Row */}
            <div className="bg-emerald-100 border-2 border-emerald-500/15 rounded-xl p-4 mb-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-1">
                  <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Position</span>
                  <span className="text-lg font-semibold text-slate-900">{jobTitle || effectiveAnalysisResults?.metadata?.target_role || 'Target Position'}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Company</span>
                  <span className="text-lg font-semibold text-slate-900">{companyName || effectiveAnalysisResults?.metadata?.target_company || 'Target Company'}</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white py-2 px-4 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-md">
                <CheckCircle className="w-4 h-4" />
                Successfully Optimized!
              </div>
            </div>

            {/* Three Score Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Before Card - Red */}
              <div className="bg-white border-2 border-red-500 rounded-xl p-5 flex flex-col items-center relative">
                <span className="absolute -top-3 bg-red-600 text-white py-1 px-3 rounded text-xs font-bold uppercase tracking-wide">Before</span>
                <div className="text-sm font-bold text-red-600 mt-2 mb-3">Original Resume</div>
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="16" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                    <circle 
                      cx="18" cy="18" r="16" fill="none" 
                      stroke="#dc2626" strokeWidth="3" 
                      strokeDasharray={`${(beforeScore || 0)} 100`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-extrabold text-slate-900">{beforeScore || 0}%</span>
                    <span className="text-xs text-slate-500">Match</span>
                  </div>
                </div>
              </div>

              {/* After Card - Green */}
              <div className="bg-white border-2 border-emerald-600 rounded-xl p-5 flex flex-col items-center relative">
                <span className="absolute -top-3 bg-emerald-600 text-white py-1 px-3 rounded text-xs font-bold uppercase tracking-wide">After</span>
                <div className="text-sm font-bold text-emerald-600 mt-2 mb-3">Tailored Resume</div>
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="16" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                    <circle 
                      cx="18" cy="18" r="16" fill="none" 
                      stroke="#059669" strokeWidth="3" 
                      strokeDasharray={`${(afterScore || 0)} 100`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-extrabold text-slate-900">{afterScore || 0}%</span>
                    <span className="text-xs text-slate-500">Match</span>
                  </div>
                </div>
              </div>

              {/* Boost Card - Violet/Gradient */}
              <div className="bg-white border-2 border-violet-500 rounded-xl p-5 flex flex-col items-center">
                <div className="text-sm font-bold text-violet-600 mb-3">Performance Boost</div>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl p-4 text-white flex flex-col items-center w-full">
                  <span className="text-3xl font-extrabold">+{improvement || 0}</span>
                  <span className="text-xs opacity-90">Points Gained</span>
                  <div className="w-full h-1.5 bg-white/20 rounded mt-2">
                    <div className="h-full bg-white rounded transition-all duration-500" style={{ width: `${Math.min(100, (afterScore || 0))}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Overview Banner */}
          {effectiveAnalysisResults?.overview && (
            <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-emerald-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Transformation Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-gray-700">{effectiveAnalysisResults.overview}</p>
              </CardContent>
            </Card>
          )}

          {/* Detailed Score Dashboard with Tabs - Only for new structure */}
          {hasNewStructure && (
            <Card className="border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Detailed Score Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Score Cards Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                  <ScoreCard 
                    label="Job Title Alignment" 
                    score={scores?.job_title_alignment}
                    colorClass="bg-blue-50 border-blue-200"
                  />
                  <ScoreCard 
                    label="Skills Match" 
                    score={scores?.skills_match}
                    colorClass="bg-purple-50 border-purple-200"
                  />
                  <ScoreCard 
                    label="Experience Relevance" 
                    score={scores?.experience_relevance}
                    colorClass="bg-indigo-50 border-indigo-200"
                  />
                  <ScoreCard 
                    label="Keyword Coverage" 
                    score={scores?.keyword_coverage}
                    colorClass="bg-teal-50 border-teal-200"
                  />
                  <ScoreCard 
                    label="Problem-Solution" 
                    score={scores?.problem_solution_positioning}
                    colorClass="bg-emerald-50 border-emerald-200"
                  />
                </div>

                {/* Tabbed Sections */}
                <Tabs defaultValue="problems" className="w-full">
                  <TabsList className="grid w-full grid-cols-4 h-auto">
                    <TabsTrigger value="problems" className="text-xs py-2 px-2 flex flex-col gap-1 items-center">
                      <Target className="w-4 h-4" />
                      <span className="hidden sm:inline">Problems Solved</span>
                      <span className="sm:hidden">Problems</span>
                    </TabsTrigger>
                    <TabsTrigger value="changes" className="text-xs py-2 px-2 flex flex-col gap-1 items-center">
                      <FileText className="w-4 h-4" />
                      <span className="hidden sm:inline">What Changed</span>
                      <span className="sm:hidden">Changes</span>
                    </TabsTrigger>
                    <TabsTrigger value="keywords" className="text-xs py-2 px-2 flex flex-col gap-1 items-center">
                      <Key className="w-4 h-4" />
                      <span className="hidden sm:inline">Keywords</span>
                      <span className="sm:hidden">Keywords</span>
                    </TabsTrigger>
                    <TabsTrigger value="impact" className="text-xs py-2 px-2 flex flex-col gap-1 items-center">
                      <Lightbulb className="w-4 h-4" />
                      <span className="hidden sm:inline">Why It Matters</span>
                      <span className="sm:hidden">Impact</span>
                    </TabsTrigger>
                  </TabsList>

                  {/* Problems You Now Solve Tab */}
                  <TabsContent value="problems" className="mt-4">
                    {effectiveAnalysisResults?.problems_you_now_solve?.problems && effectiveAnalysisResults.problems_you_now_solve.problems.length > 0 ? (
                      <div className="space-y-4">
                        {effectiveAnalysisResults.problems_you_now_solve.summary && (
                          <p className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                            {effectiveAnalysisResults.problems_you_now_solve.summary}
                          </p>
                        )}
                        {effectiveAnalysisResults.problems_you_now_solve.problems.map((problem, i) => (
                          <div key={i} className="p-4 bg-white rounded-lg border border-emerald-200 shadow-sm">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <h5 className="font-semibold text-red-700 text-sm mb-2 flex items-center gap-1">
                                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                  Their Problem:
                                </h5>
                                <p className="text-sm text-gray-700">{problem.their_problem}</p>
                              </div>
                              <div>
                                <h5 className="font-semibold text-emerald-700 text-sm mb-2 flex items-center gap-1">
                                  <CheckCircle className="w-4 h-4" />
                                  Your Solution:
                                </h5>
                                <p className="text-sm text-gray-700">{problem.your_solution}</p>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-emerald-100 grid md:grid-cols-2 gap-4 text-xs">
                              <div>
                                <span className="font-medium text-blue-600">Where it appears:</span>{' '}
                                <span className="text-gray-600">{problem.where_it_appears}</span>
                              </div>
                              <div>
                                <span className="font-medium text-purple-600">What changed:</span>{' '}
                                <span className="text-gray-600">{problem.what_changed}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No problem-solution data available</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* What Changed Tab */}
                  <TabsContent value="changes" className="mt-4">
                    {effectiveAnalysisResults?.what_changed ? (
                      <Accordion type="multiple" className="w-full" defaultValue={["career-summary", "skills-section"]}>
                        {/* Career Summary */}
                        {effectiveAnalysisResults.what_changed.career_summary && (
                          <AccordionItem value="career-summary">
                            <AccordionTrigger className="text-sm font-semibold text-blue-700 hover:no-underline">
                              Career Summary Changes
                            </AccordionTrigger>
                            <AccordionContent className="space-y-4">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                                  <h5 className="font-semibold text-red-700 text-sm mb-2">Before</h5>
                                  <p className="text-sm text-gray-700">{effectiveAnalysisResults.what_changed.career_summary.before}</p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                  <h5 className="font-semibold text-emerald-700 text-sm mb-2">After</h5>
                                  <p className="text-sm text-gray-700">{effectiveAnalysisResults.what_changed.career_summary.after}</p>
                                </div>
                              </div>
                              
                              {effectiveAnalysisResults.what_changed.career_summary.problems_now_named && effectiveAnalysisResults.what_changed.career_summary.problems_now_named.length > 0 && (
                                <div>
                                  <h5 className="font-semibold text-sm mb-2">Problems Now Named:</h5>
                                  <div className="flex flex-wrap gap-2">
                                    {effectiveAnalysisResults.what_changed.career_summary.problems_now_named.map((p, i) => (
                                      <Badge key={i} variant="secondary" className="bg-emerald-100 text-emerald-800">{p}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {effectiveAnalysisResults.what_changed.career_summary.metrics_highlighted && effectiveAnalysisResults.what_changed.career_summary.metrics_highlighted.length > 0 && (
                                <div>
                                  <h5 className="font-semibold text-sm mb-2">Metrics Highlighted:</h5>
                                  <div className="flex flex-wrap gap-2">
                                    {effectiveAnalysisResults.what_changed.career_summary.metrics_highlighted.map((m, i) => (
                                      <Badge key={i} variant="outline" className="border-blue-300 text-blue-700">{m}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {/* Career Highlights */}
                        {effectiveAnalysisResults.what_changed.career_highlights && (
                          <AccordionItem value="career-highlights">
                            <AccordionTrigger className="text-sm font-semibold text-purple-700 hover:no-underline">
                              Career Highlights Changes
                            </AccordionTrigger>
                            <AccordionContent className="space-y-4">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                                  <h5 className="font-semibold text-red-700 text-sm mb-2">Before</h5>
                                  <p className="text-sm text-gray-700">{effectiveAnalysisResults.what_changed.career_highlights.before}</p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                  <h5 className="font-semibold text-emerald-700 text-sm mb-2">After</h5>
                                  <p className="text-sm text-gray-700">{effectiveAnalysisResults.what_changed.career_highlights.after}</p>
                                </div>
                              </div>

                              {effectiveAnalysisResults.what_changed.career_highlights.new_micro_headings && effectiveAnalysisResults.what_changed.career_highlights.new_micro_headings.length > 0 && (
                                <div>
                                  <h5 className="font-semibold text-sm mb-2">New Micro-Headings Added:</h5>
                                  <div className="flex flex-wrap gap-2">
                                    {effectiveAnalysisResults.what_changed.career_highlights.new_micro_headings.map((h, i) => (
                                      <Badge key={i} variant="secondary" className="bg-purple-100 text-purple-800">{h}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {effectiveAnalysisResults.what_changed.career_highlights.key_changes && effectiveAnalysisResults.what_changed.career_highlights.key_changes.length > 0 && (
                                <div>
                                  <h5 className="font-semibold text-sm mb-2">Key Changes:</h5>
                                  <ul className="space-y-2">
                                    {effectiveAnalysisResults.what_changed.career_highlights.key_changes.map((change, i) => (
                                      <li key={i} className="flex items-start gap-2 text-sm">
                                        <ArrowRight className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                                        <span>{change}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {/* Skills Section */}
                        {effectiveAnalysisResults.what_changed.skills_section && (
                          <AccordionItem value="skills-section">
                            <AccordionTrigger className="text-sm font-semibold text-teal-700 hover:no-underline">
                              Skills Section Changes
                            </AccordionTrigger>
                            <AccordionContent className="space-y-4">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <h5 className="font-semibold text-emerald-700 text-sm mb-2 flex items-center gap-1">
                                    <CheckCircle className="w-4 h-4" />
                                    Skills Added
                                  </h5>
                                  <div className="flex flex-wrap gap-2">
                                    {effectiveAnalysisResults.what_changed.skills_section.skills_added?.map((skill, i) => (
                                      <Badge key={i} variant="secondary" className="bg-emerald-100 text-emerald-800">+ {skill}</Badge>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <h5 className="font-semibold text-red-700 text-sm mb-2">Skills Removed</h5>
                                  <div className="flex flex-wrap gap-2">
                                    {effectiveAnalysisResults.what_changed.skills_section.skills_removed?.map((skill, i) => (
                                      <Badge key={i} variant="outline" className="border-red-300 text-red-700">- {skill}</Badge>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              
                              {effectiveAnalysisResults.what_changed.skills_section.why_it_matters && (
                                <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                                  <h5 className="font-semibold text-teal-800 text-sm mb-1">Why It Matters</h5>
                                  <p className="text-sm text-teal-700">{effectiveAnalysisResults.what_changed.skills_section.why_it_matters}</p>
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {/* Professional Experience */}
                        {effectiveAnalysisResults.what_changed.professional_experience && (
                          <AccordionItem value="professional-experience">
                            <AccordionTrigger className="text-sm font-semibold text-indigo-700 hover:no-underline">
                              Professional Experience Changes
                            </AccordionTrigger>
                            <AccordionContent className="space-y-4">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                                  <h5 className="font-semibold text-red-700 text-sm mb-2">Before</h5>
                                  <p className="text-sm text-gray-700">{effectiveAnalysisResults.what_changed.professional_experience.before}</p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                  <h5 className="font-semibold text-emerald-700 text-sm mb-2">After</h5>
                                  <p className="text-sm text-gray-700">{effectiveAnalysisResults.what_changed.professional_experience.after}</p>
                                </div>
                              </div>

                              {effectiveAnalysisResults.what_changed.professional_experience.key_changes && effectiveAnalysisResults.what_changed.professional_experience.key_changes.length > 0 && (
                                <div className="space-y-3">
                                  <h5 className="font-semibold text-sm">Position-by-Position Changes:</h5>
                                  {effectiveAnalysisResults.what_changed.professional_experience.key_changes.map((posChange, i) => (
                                    <div key={i} className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                                      <h6 className="font-semibold text-indigo-800 text-sm mb-2">{posChange.position}</h6>
                                      <ul className="space-y-1">
                                        {posChange.changes.map((change, j) => (
                                          <li key={j} className="flex items-start gap-2 text-xs">
                                            <ArrowRight className="w-3 h-3 text-indigo-600 mt-0.5 shrink-0" />
                                            <span>{change}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        )}
                      </Accordion>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No change details available</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Keywords Tab */}
                  <TabsContent value="keywords" className="mt-4">
                    {effectiveAnalysisResults?.keywords_added ? (
                      <div className="space-y-4">
                        {effectiveAnalysisResults.keywords_added.summary && (
                          <p className="text-sm text-teal-700 bg-teal-50 p-3 rounded-lg border border-teal-200">
                            {effectiveAnalysisResults.keywords_added.summary}
                          </p>
                        )}
                        
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Coverage</span>
                          <span className="font-bold text-teal-600">{effectiveAnalysisResults.keywords_added.coverage_percentage}%</span>
                        </div>
                        <Progress value={effectiveAnalysisResults.keywords_added.coverage_percentage || 0} className="h-3" />
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-semibold text-emerald-700 text-sm mb-2">Keywords Added</h5>
                            <div className="flex flex-wrap gap-1">
                              {effectiveAnalysisResults.keywords_added.keywords_added?.map((k, i) => (
                                <Badge key={i} variant="secondary" className="bg-emerald-100 text-emerald-800 text-xs">+ {k}</Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h5 className="font-semibold text-orange-700 text-sm mb-2">Still Missing</h5>
                            <div className="flex flex-wrap gap-1">
                              {effectiveAnalysisResults.keywords_added.keywords_still_missing?.map((k, i) => (
                                <Badge key={i} variant="outline" className="border-orange-300 text-orange-700 text-xs">{k}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No keyword data available</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Why It Matters Tab */}
                  <TabsContent value="impact" className="mt-4">
                    {effectiveAnalysisResults?.why_this_matters ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <h5 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            Before Tailoring
                          </h5>
                          <p className="text-sm text-gray-700">{effectiveAnalysisResults.why_this_matters.before}</p>
                        </div>
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                          <h5 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            After Tailoring
                          </h5>
                          <p className="text-sm text-gray-700">{effectiveAnalysisResults.why_this_matters.after}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No impact analysis available</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}

          {/* Legacy: What Was Changed (for old changesAnalysis format) */}
          {!hasNewStructure && changesAnalysis && (
            <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50/50 to-green-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <Target className="w-5 h-5" />
                  What Was Changed
                </CardTitle>
                <CardDescription>Summary of improvements made to your resume</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Keywords Added */}
                {changesAnalysis.keywords_added && changesAnalysis.keywords_added.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      Keywords Added
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {changesAnalysis.keywords_added.map((keyword: string, i: number) => (
                        <Badge key={i} variant="secondary" className="bg-emerald-100 text-emerald-800 border-emerald-200">
                          + {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Skills Emphasized */}
                {changesAnalysis.skills_emphasized && changesAnalysis.skills_emphasized.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      Skills Emphasized
                    </h4>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <ul className="space-y-2">
                        {changesAnalysis.skills_emphasized.map((item: any, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                            <span>
                              {typeof item === 'string' ? item : (
                                <><strong>{item.skill}:</strong> {item.detail}</>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                
                {/* Experience Reframed */}
                {changesAnalysis.experience_reframed && changesAnalysis.experience_reframed.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-purple-700 mb-2 flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Experience Reframed
                    </h4>
                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <ul className="space-y-2">
                        {changesAnalysis.experience_reframed.map((item: any, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <TrendingUp className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                            <span>
                              {typeof item === 'string' ? item : (
                                <><strong>{item.original}:</strong> {item.reframed}</>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                
                {/* Overall Improvements */}
                {changesAnalysis.overall_improvements && changesAnalysis.overall_improvements.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-indigo-700 mb-2 flex items-center gap-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                      Overall Improvements
                    </h4>
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                      <ul className="space-y-2">
                        {changesAnalysis.overall_improvements.map((improvement: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <TrendingUp className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                            <span>{improvement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                
                {/* Summary text if provided */}
                {changesAnalysis.summary && (
                  <div className="bg-emerald-100 p-4 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-2">Changes Summary</h4>
                    <p className="text-sm text-emerald-700">{changesAnalysis.summary}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Metadata Footer */}
          {effectiveAnalysisResults?.metadata && (
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-4 border-t">
              {effectiveAnalysisResults.metadata.target_role && (
                <span><strong>Target Role:</strong> {effectiveAnalysisResults.metadata.target_role}</span>
              )}
              {effectiveAnalysisResults.metadata.target_company && (
                <span><strong>Company:</strong> {effectiveAnalysisResults.metadata.target_company}</span>
              )}
              {effectiveAnalysisResults.metadata.target_industry && (
                <span><strong>Industry:</strong> {effectiveAnalysisResults.metadata.target_industry}</span>
              )}
              {effectiveAnalysisResults.metadata.analysis_date && (
                <span><strong>Analysis Date:</strong> {effectiveAnalysisResults.metadata.analysis_date}</span>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClaudeMatchAnalysisModal;
